import { AiUsageRecordRow } from "../types/MeGenTypes";
import { usageModelOf } from "./aiUsageModels";

/**
 * Pivot helpers ของ MeGenCost dashboard — ทุกแท็บ (รวม/รายวัน/ราย thread/ราย model/ราย user)
 * คำนวณจาก records ชุดเดียวกันด้วยฟังก์ชันนี้ ให้ตัวเลขสอดคล้องกันเสมอ
 */

export type AiUsageAggregate = {
  key: string;
  calls: number;
  images: number;
  video_seconds: number;
  audio_seconds: number;
  input_tokens: number;
  output_tokens: number;
  errors: number;
  cost_usd: number; // USD จาก rate ต่อ model (aiModelCost) — 0 เมื่อ caller ไม่ส่ง costFn
  process_seconds: number; // เวลาเครื่องรวม (duration_ms/1000) — ใช้แสดงกับแถว GPU hub
  first_used: string | null;
  last_used: string | null;
  users: string[];
  threads: string[];
};

export type AiUsageMetricKey =
  | "calls"
  | "images"
  | "video_seconds"
  | "audio_seconds"
  | "input_tokens"
  | "output_tokens"
  | "errors"
  | "cost_usd";

// เงิน: ≥ $1 → 2 ตำแหน่ง, น้อยกว่า $1 → สูงสุด 4 ตำแหน่ง (0.0793 ต้องไม่กลายเป็น 0.08) — ใช้กับค่าใช้จ่ายทุกที่
export const formatMoney = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) return "$0.00";
  const abs = Math.abs(value);
  const text = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: abs >= 1 ? 2 : 4,
  });
  return `${value < 0 ? "-" : ""}$${text}`;
};

export type AiUsageMetric = {
  key: AiUsageMetricKey;
  label: string;
  unit: string;
  format?: (value: number) => string; // ไม่ระบุ = formatMetric
};

// ลำดับ = ลำดับคอลัมน์ในตาราง/ตัวเลือก metric — ค่าใช้จ่ายอยู่ถัดจาก calls เพราะเป็นตัวเลขหลักที่ผู้ใช้ดู
export const AI_USAGE_METRICS: AiUsageMetric[] = [
  { key: "calls", label: "จำนวน Call", unit: "ครั้ง" },
  { key: "cost_usd", label: "ค่าใช้จ่าย", unit: "USD", format: formatMoney },
  { key: "images", label: "รูปภาพ", unit: "รูป" },
  { key: "video_seconds", label: "วิดีโอ", unit: "วินาที" },
  { key: "audio_seconds", label: "เสียง", unit: "วินาที" },
  { key: "input_tokens", label: "Tokens เข้า", unit: "tokens" },
  { key: "output_tokens", label: "Tokens ออก", unit: "tokens" },
  { key: "errors", label: "Errors", unit: "ครั้ง" },
];

// ป้ายชื่อ metric พร้อมหน่วยกำกับ เช่น "วิดีโอ (วินาที)" — ใช้กับหัวตาราง/แกนกราฟ/tooltip
export const metricLabelWithUnit = (key: AiUsageMetricKey): string => {
  const m = AI_USAGE_METRICS.find((x) => x.key === key);
  if (!m) return key;
  return `${m.label} (${m.unit})`;
};

export const metricUnitOf = (key: AiUsageMetricKey): string =>
  AI_USAGE_METRICS.find((x) => x.key === key)?.unit || "";

// --- Cloud buckets: ปลายทางเงินตาม provider (ใช้กับ filter ชั้นที่ 2 ของ dashboard) ---
// comfyui = GPU hub (คิดตามเวลาเครื่อง) แยกจาก local (เครื่องตัวเอง ฟรี)
export const CLOUD_BUCKETS: { key: string; label: string; providers: string[] }[] = [
  { key: "alibaba", label: "Alibaba Cloud", providers: ["dashscope"] },
  { key: "google", label: "Google Cloud", providers: ["gemini", "google"] },
  { key: "gpu_hub", label: "GPU Hub (ComfyUI / FFmpeg)", providers: ["comfyui", "gpuhub"] },
  { key: "local", label: "Local GPU", providers: ["local"] },
];

export const cloudBucketOf = (provider: string | null | undefined): string => {
  const bucket = CLOUD_BUCKETS.find((b) => b.providers.includes(provider || ""));
  return bucket ? bucket.key : "other";
};

// --- ชื่อประเภทงานต่อ service (ใช้ใน breakdown การ์ด AI Calls) ---
const SERVICE_LABELS: Record<string, string> = {
  tts: "Text to Speech",
  image: "Image Generation",
  video: "Video Generation",
  llm: "Large Language Model",
  ffmpeg: "FFmpeg",
};

export const serviceLabelOf = (service: string): string => SERVICE_LABELS[service] || service;

export type AiUsageGroupKey = "service" | "provider" | "model" | "user";

export const AI_USAGE_GROUPS: { key: AiUsageGroupKey; label: string }[] = [
  { key: "service", label: "Service" },
  { key: "provider", label: "Provider" },
  { key: "model", label: "Model" },
  { key: "user", label: "User" },
];

export const groupKeyOf = (r: AiUsageRecordRow, group: AiUsageGroupKey): string => {
  if (group === "service") return r.service || "unknown";
  if (group === "provider") return r.provider || "unknown";
  if (group === "model") return usageModelOf(r) || "unknown";
  return r.user_id || "unknown";
};

const pad2 = (n: number) => String(n).padStart(2, "0");

// created_at จาก backend เป็น UTC (มี +00:00) — ต้องแปลงเป็นเวลาท้องถิ่น (ไทย) ก่อนตัดวัน/แสดงผล
// ค่าแบบ naive (ไม่มี timezone) new Date() ตีความเป็น local อยู่แล้ว
export const toLocalDay = (iso: string | null): string => {
  if (!iso) return "unknown";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10) || "unknown";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const toLocalDateTime = (iso: string | null, opts?: { seconds?: boolean }): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.replace("T", " ").slice(0, opts?.seconds ? 19 : 16);
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${toLocalDay(iso)} ${hm}${opts?.seconds ? `:${pad2(d.getSeconds())}` : ""}`;
};

export const dayOf = (r: AiUsageRecordRow): string => toLocalDay(r.created_at);

export const aggregateRecords = (
  records: AiUsageRecordRow[],
  keyFn: (r: AiUsageRecordRow) => string,
  costFn?: (r: AiUsageRecordRow) => number // USD ต่อ record (aiModelCost.makeRecordCostFn) — ไม่ส่ง = 0
): AiUsageAggregate[] => {
  const map: Record<string, AiUsageAggregate> = Object.create(null);
  const users = new Map<string, Set<string>>();
  const threads = new Map<string, Set<string>>();

  for (const r of records) {
    const key = keyFn(r);
    let agg = map[key];

    if (!agg) {
      users.set(key, new Set());
      threads.set(key, new Set());
      agg = map[key] = {
        key,
        calls: 0,
        images: 0,
        video_seconds: 0,
        audio_seconds: 0,
        input_tokens: 0,
        output_tokens: 0,
        errors: 0,
        cost_usd: 0,
        process_seconds: 0,
        first_used: null,
        last_used: null,
        users: [],
        threads: [],
      };
    }

    agg.calls += 1;
    agg.images += r.image_count || 0;
    agg.video_seconds += r.video_duration || 0;
    agg.audio_seconds += r.audio_duration || 0;
    agg.input_tokens += r.input_tokens || 0;
    agg.output_tokens += r.output_tokens || 0;
    if (r.ffmpeg_task ? r.ffmpeg_task.status === "error" : r.status && r.status !== "success") agg.errors += 1;
    agg.cost_usd += costFn ? costFn(r) : 0;
    agg.process_seconds += (r.duration_ms || 0) / 1000;

    if (r.created_at) {
      if (!agg.first_used || r.created_at < agg.first_used) agg.first_used = r.created_at;
      if (!agg.last_used || r.created_at > agg.last_used) agg.last_used = r.created_at;
    }
    if (r.user_id && !users.get(key)!.has(r.user_id)) { users.get(key)!.add(r.user_id); agg.users.push(r.user_id); }
    if (r.thread_id && !threads.get(key)!.has(r.thread_id)) { threads.get(key)!.add(r.thread_id); agg.threads.push(r.thread_id); }
  }

  return Object.values(map);
};

export const grandTotals = (
  records: AiUsageRecordRow[],
  costFn?: (r: AiUsageRecordRow) => number
): AiUsageAggregate =>
  aggregateRecords(records, () => "total", costFn)[0] || {
    key: "total",
    calls: 0,
    images: 0,
    video_seconds: 0,
    audio_seconds: 0,
    input_tokens: 0,
    output_tokens: 0,
    errors: 0,
    cost_usd: 0,
    process_seconds: 0,
    first_used: null,
    last_used: null,
    users: [],
    threads: [],
  };

// แสดงตัวเลขให้อ่านง่าย: จำนวนเต็มมี comma, ทศนิยม 2 ตำแหน่งเมื่อจำเป็น
export const formatMetric = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

// format ตาม metric: cost_usd → เงิน, อื่น ๆ → formatMetric (ใช้กับการ์ด/ตาราง/tooltip ให้สม่ำเสมอ)
export const formatMetricValue = (key: AiUsageMetricKey, value: number): string => {
  const m = AI_USAGE_METRICS.find((x) => x.key === key);
  return (m?.format || formatMetric)(value);
};
